import { FREE_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { hasAssetsEntitlement } from '@jira/servicedesk__insight-common-services-workspace-context/src/fetchWorkspaceId.tsx';
import { useAssetsWorkspaceHost } from '@atlassian/assets-workspace-host/use-assets-workspace-host';
import { getProductContext } from '@atlassian/assets-workspace-host/get-product-context';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type UseAssetsSidebarState = {
	isVisible: boolean;
	isEntitled: boolean;
	isUpsell: boolean;
	isJsmFreeSiteAdminUpsell: boolean;
	/** The Units workspace-host resolver succeeded for this route (cross-unit Assets reachable). */
	isUnitsResolved: boolean;
	/** Units-aware absolute-URL builder; identity when the resolver has not resolved. */
	toResolvedAbsoluteUrl: (relativePath: string) => string;
};

/**
 * Single source of truth for the Assets sidebar signals, shared by every touchpoint so the
 * registry, the rendered item, and the customization modal never diverge. JSM Standard/Premium
 * are assumed provisioned, so they resolve via `hasAssetsEntitlement` with no paid-JSM branch.
 */
export const useAssetsSidebarState = (): UseAssetsSidebarState => {
	const appEditions = useAppEditions();
	const licensedProducts = useLicensedProducts();
	const cloudId = useCloudId();
	const appPermissions = useApplicationPermissions();

	// `isSuccess` already implies the resolver gate is on (the hook is skipped when off);
	// `toResolvedAbsoluteUrl` is identity when not resolved.
	const { isSuccess: isUnitsResolved, toResolvedAbsoluteUrl } = useAssetsWorkspaceHost({
		cloudId,
		productContext: getProductContext({ appPermissions }),
		skip: !fg('astral_units_workspace_host_resolver'),
	});

	const isSiteAdmin = useIsSiteAdmin();

	const isJsmFree = appEditions.serviceDesk === FREE_EDITION;
	// `useApplicationPermissions` resolves to null until the tenant context store is populated,
	// and the nav renders before that on some routes.
	const hasJiraOrJsmProduct = Boolean(
		appPermissions?.hasServiceDeskAccess || appPermissions?.hasSoftwareAccess,
	);

	const isEntitled = isUnitsResolved || hasAssetsEntitlement({ licensedProducts });

	const isUpsell = !isEntitled && hasJiraOrJsmProduct && fg('assets-platform-landing-page');

	const isJsmFreeSiteAdminUpsell =
		isSiteAdmin && isJsmFree && fg('sqa-2098-assets-nav-upsell-jsm-free-nav');

	const isVisible = isEntitled || isUpsell || isJsmFreeSiteAdminUpsell;

	return {
		isVisible,
		isEntitled,
		isUpsell,
		isJsmFreeSiteAdminUpsell,
		isUnitsResolved,
		toResolvedAbsoluteUrl,
	};
};
